import { AnchorElement, color, rem } from '@thiememeulenhoff/lit-sdk';
import { customElement, property, state } from 'lit/decorators.js';
import { css, html, nothing } from 'lit';
import { tagName } from '../../../helpers';
import { PanelComponent } from './panel.component';

const tag = tagName('tab');

@customElement(tag)
export class TabComponent extends AnchorElement {
  static override styles = [
    css`
      :where(span.stripe) {
        background: transparent;
        block-size: ${rem(4)};
        border-radius: ${rem(8)};
        inset-block-end: ${rem(-1)};
        inset-inline: 0;
        position: absolute;
      }

      :where(label) {
        align-items: center;
        cursor: pointer;
        display: inline-flex;
        letter-spacing: 0.05em;
        padding: ${rem(6)} 0;
        position: relative;

        &:hover span.stripe {
          background: ${color('grey', 600)};
        }
      }

      :host([selected]) {
        font-weight: bold;

        span.stripe {
          background: ${color('grey', 800)};
        }
      }

      a {
        color: inherit;
        text-decoration: none;
      }

      a:focus-visible {
        outline-color: var(--tm-focus-ring-color);
        outline-offset: var(--tm-focus-ring-offset);
        outline-style: var(--tm-focus-ring-style);
        outline-width: var(--tm-focus-ring-width);
      }
    `,
  ];

  @property({ type: String, reflect: true })
  readonly for!: string;

  @property({ type: Boolean, reflect: true, attribute: 'selected' })
  @state()
  selectedInternal = false;

  readonly #panels: Array<PanelComponent> = [];

  get selected(): boolean {
    return this.selectedInternal;
  }

  set selected(value: boolean) {
    const oldValue = this.selectedInternal;
    this.selectedInternal = value;
    this.requestUpdate('selected', oldValue);

    if (!value) {
      return;
    }

    this.dispatchEvent(
      new CustomEvent('selected', {
        bubbles: true,
        composed: true,
        detail: { selected: this.selectedInternal, for: this.for },
      }),
    );
  }

  override render() {
    let slot = html`<slot></slot>`;

    if (this.href) {
      slot = html`<a
        part="tab"
        class="tab"
        href=${this.disabled ? nothing : this.href}
        target=${this.target ?? nothing}
        ?disabled=${this.disabled}
        data-test="anchor"
      >
        <slot></slot>
      </a>`;
    }

    return html`<label part="tab">
      ${slot}
      <span class="stripe"></span>
    </label>`;
  }

  protected override onCreate = () => {
    this.addEventListener('click', () => this.setSelected(true));
  };

  public setSelected(selected: boolean) {
    this.selected = selected;
    this.#onSelectedChange();
  }

  public addPanel(panel: PanelComponent) {
    if (!this.#panels.includes(panel)) {
      this.#panels.push(panel);
    }

    panel.setVisible(this.selected);
  }

  #onSelectedChange() {
    this.#panels.forEach((panel) => {
      panel.setVisible(this.selected);
    });
  }

  override connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'tab');
  }
}
